import { useState, useEffect, useCallback } from 'react';
import {
  listDocuments,
  getDocumentById,
  getDocumentStatus,
  getExtractedKpis,
  getSavedKpis,
} from '../services/docSenseService';

export const useDocuments = (filters = {}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await listDocuments(filters);
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, setData, isLoading, error, refetch: fetch };
};

export const useDocument = (id) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await getDocumentById(id);
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
};

export const useDocumentStatus = (id, active = true) => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!id || !active) return;

    const poll = async () => {
      try {
        const result = await getDocumentStatus(id);
        if (result) setStatus(result);
        return result?.status;
      } catch (e) {
        console.error('Status poll error:', e);
      }
    };

    poll();
    const interval = setInterval(async () => {
      const st = await poll();
      if (st === 'COMPLETED' || st === 'FAILED') clearInterval(interval);
    }, 5000);

    return () => clearInterval(interval);
  }, [id, active]);

  return { status };
};

/**
 * AI-extracted KPIs — fetched from /extracted-kpis.
 * Only called when documentId is non-null (i.e. after COMPLETED).
 */
export const useExtractedKpis = (id) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await getExtractedKpis(id);
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, setData, isLoading, error, refetch: fetch };
};

/**
 * Already-saved (confirmed) KPIs — fetched from /kpis.
 */
export const useSavedKpis = (id) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await getSavedKpis(id);
      setData(result);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
};